import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card, PageHeader, Btn, Input, Select, Pill } from "../../components/UI";
import { toast } from "sonner";
import { Globe, Plus, Trash2, ExternalLink, Loader2, Eye, EyeOff, Copy, Sparkles } from "lucide-react";
import { useChecklistReturn, ChecklistBackLink } from "../../lib/checklistReturn";

export default function Microsites() {
  const { fromChecklist, returnToChecklist } = useChecklistReturn();
  const [sites, setSites] = useState([]);
  const [pages, setPages] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ slug: "", name: "", landing_page_id: "", fundraising_campaign_id: "" });
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [filling, setFilling] = useState(false);

  const autofill = async () => {
    setFilling(true);
    try {
      const { data } = await api.get("/microsites/autofill");
      setForm(f => ({
        ...f,
        name: data.name || f.name,
        slug: data.slug || f.slug,
        landing_page_id: data.landing_page_id || f.landing_page_id,
        fundraising_campaign_id: data.fundraising_campaign_id || f.fundraising_campaign_id,
      }));
      if (data.missing?.length) toast.info(`Auto-filled what we could — you still need ${data.missing.join(" and ")}`);
      else toast.success("Auto-filled from your campaign — review and publish");
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-fill failed"); }
    finally { setFilling(false); }
  };

  const load = async () => {
    const [s, p, c] = await Promise.all([
      api.get("/microsites"),
      api.get("/fundraising/landing-pages"),
      api.get("/fundraising/campaigns"),
    ]);
    setSites(s.data); setPages(p.data); setCampaigns(c.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (fromChecklist) { setShowForm(true); autofill(); }
    // eslint-disable-next-line
  }, [fromChecklist]);

  const create = async () => {
    if (!form.slug || !form.name || !form.landing_page_id || !form.fundraising_campaign_id) {
      toast.error("All fields are required"); return;
    }
    setCreating(true);
    try {
      await api.post("/microsites", { ...form, published: true });
      toast.success(`Microsite live at /m/${form.slug}`);
      setForm({ slug: "", name: "", landing_page_id: "", fundraising_campaign_id: "" });
      setShowForm(false);
      if (returnToChecklist()) return;
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create microsite");
    } finally { setCreating(false); }
  };

  const toggle = async (m) => { await api.post(`/microsites/${m.id}/toggle`); load(); };
  const del = async (m) => { if (window.confirm("Delete this microsite?")) { await api.delete(`/microsites/${m.id}`); load(); } };
  const canonical = (m) => `https://${m.slug}.totalreach.us`;
  const copyLink = (m) => { navigator.clipboard.writeText(canonical(m)); toast.success("Link copied"); };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <ChecklistBackLink show={fromChecklist} />
      <PageHeader
        eyebrow="Fundraising · Web"
        title="Microsites"
        subtitle="Publish AI-generated landing pages as live donation microsites — each at its own candidate.totalreach.us subdomain."
        action={<Btn onClick={() => setShowForm(!showForm)} data-testid="new-microsite-btn"><Plus className="w-4 h-4" /> New microsite</Btn>}
      />

      {showForm && (
        <Card className="border-cyan-400/30">
          <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-slate-400">Fill the fields yourself, or let AI populate them from your campaign.</div>
            <Btn variant="secondary" className="!px-3 !py-1.5 text-xs" onClick={autofill} disabled={filling} data-testid="ms-autofill-btn">
              {filling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Auto-fill with AI
            </Btn>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input label="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Voss for Senate — Main" data-testid="ms-name-input" />
            <Input label="Slug (URL path)" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="voss-for-senate" data-testid="ms-slug-input" />
            <Select label="Landing page" value={form.landing_page_id} onChange={e => setForm({ ...form, landing_page_id: e.target.value })} data-testid="ms-page-select">
              <option value="">Select page…</option>
              {pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            <Select label="Fundraising campaign" value={form.fundraising_campaign_id} onChange={e => setForm({ ...form, fundraising_campaign_id: e.target.value })} data-testid="ms-campaign-select">
              <option value="">Select campaign…</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn onClick={create} disabled={creating} data-testid="create-microsite-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />} Publish microsite
            </Btn>
          </div>
        </Card>
      )}

      <Card padded={false}>
        <table className="w-full text-sm" data-testid="microsites-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-5 py-3">Microsite</th>
              <th className="px-5 py-3">URL</th>
              <th className="px-5 py-3">Visits</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sites.map(m => (
              <tr key={m.id} className="border-b border-white/5">
                <td className="px-5 py-3 text-white">{m.name}</td>
                <td className="px-5 py-3">
                  <a href={canonical(m)} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline font-mono text-xs flex items-center gap-1" data-testid={`ms-link-${m.slug}`}>
                    {m.slug}.totalreach.us <ExternalLink className="w-3 h-3" />
                  </a>
                  <a href={`/m/${m.slug}`} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-slate-300 font-mono text-[10px]" data-testid={`ms-preview-link-${m.slug}`}>
                    preview: /m/{m.slug}
                  </a>
                </td>
                <td className="px-5 py-3 font-mono text-xs text-slate-400">{m.visits || 0}</td>
                <td className="px-5 py-3"><Pill status={m.published ? "Active" : "Paused"}>{m.published ? "live" : "unpublished"}</Pill></td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => copyLink(m)} className="p-1.5 text-slate-400 hover:text-cyan-300" title="Copy link" data-testid={`ms-copy-${m.slug}`}><Copy className="w-4 h-4" /></button>
                    <button onClick={() => toggle(m)} className="p-1.5 text-slate-400 hover:text-amber-300" title={m.published ? "Unpublish" : "Publish"} data-testid={`ms-toggle-${m.slug}`}>
                      {m.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => del(m)} className="p-1.5 text-slate-400 hover:text-rose-300" data-testid={`ms-delete-${m.slug}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {sites.length === 0 && <tr><td colSpan="5" className="px-5 py-8 text-center text-sm text-slate-500">No microsites yet — generate a landing page first, then publish it here.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
